// src/pages/Profiles/ProfilesPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Users } from 'lucide-react';
import ConfirmDialog from '../../components/Admin/ConfirmDialog';
import {
  listProfiles,
  createProfile,
  renameProfile,
  deleteProfile,
  setProfileMaturity,
  getActiveProfileId,
  setActiveProfileId,
  MATURITY_RATINGS,
  type ViewerProfile,
  type MaturityRating,
} from '../../services/viewerProfileService';
import { useToast } from '../../hooks/useToast';
import MainLayout from '../../components/MainLayout/MainLayout';

/**
 * The viewers on this one account.
 *
 * A profile is a label on this account, not another person's account. Nobody is invited,
 * no second login is created, and the name is not looked up against anybody - it is just
 * what this account calls that viewer, so two households can both have a "Mum" and two
 * profiles on the same account cannot.
 *
 * The first copy here read "Add someone" / "Their name", which was read as adding
 * another registered user and raised the fair question of what happens when two people
 * share a name. Nothing is matched, so nothing collides; the wording now says so.
 *
 * Each profile keeps its own place in each title, so removing one discards wherever that
 * viewer had got to in everything they were part-way through. The server refuses to
 * remove the last profile, and refuses to remove the main one - it holds everything
 * watched before profiles were set up - and both refusals are shown as the server words
 * them rather than reduced to "could not delete".
 *
 * The maturity limit only ever narrows: the account's own age check still applies and the
 * stricter of the two wins, so setting a profile to 18 on a child's account unlocks
 * nothing. The wording says "watch" rather than anything broader, because that is what it
 * covers - comments and live chat are still gated by the account alone, and a parent
 * reading more into the control than it does is the failure worth avoiding.
 */
const ProfilesPage: React.FC = () => {
  const { showToast } = useToast();
  const [profiles, setProfiles] = useState<ViewerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const [confirmRemove, setConfirmRemove] = useState<ViewerProfile | null>(null);

  const activeId = getActiveProfileId();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setProfiles(await listProfiles());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load your profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const created = await createProfile(name);
      setNewName('');
      showToast(`${created.name} added`, 'success');
      await load();
    } catch (err) {
      // The server explains its refusals - the name is taken, five is the limit - so
      // they are shown as written.
      showToast(err instanceof Error ? err.message : 'Could not add that profile', 'error');
    } finally {
      setAdding(false);
    }
  };

  const saveName = async (profile: ViewerProfile) => {
    const name = editingName.trim();
    if (!name || name === profile.name) {
      setEditingId(null);
      return;
    }
    setBusyId(profile.id);
    try {
      await renameProfile(profile.id, name);
      setEditingId(null);
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not rename it', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (profile: ViewerProfile) => {
    setBusyId(profile.id);
    try {
      await deleteProfile(profile.id);
      showToast(`${profile.name} removed`, 'success');
      await load();
    } catch (err) {
      // "This is the only profile on the account" and "the main profile cannot be
      // removed" are both whole answers, and shown for long enough to read.
      showToast(err instanceof Error ? err.message : 'Could not remove it', 'error', 8000);
    } finally {
      setBusyId(null);
      setConfirmRemove(null);
    }
  };

  const changeMaturity = async (profile: ViewerProfile, value: MaturityRating | '') => {
    setBusyId(profile.id);
    try {
      await setProfileMaturity(profile.id, value);
      showToast(value
        ? `${profile.name} can watch ${value} and under`
        : `${profile.name} has no viewing limit`, 'success');
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not change that limit', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const watchAs = (profile: ViewerProfile) => {
    setActiveProfileId(profile.isDefault ? null : profile.id);
    showToast(`Watching as ${profile.name}`, 'success');
    // Reloaded for the same reason the switcher reloads: everything keyed to a profile
    // is spread across components holding their own copies.
    window.location.reload();
  };

  // MainLayout, like every other account-facing page. Routed pages here are not wrapped
  // by App - each one brings its own header and footer - so a page that forgets it
  // renders with no navigation at all, which is what happened.
  return (
    <MainLayout>
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
          <div className="flex items-center">
            <Users className="mr-3 h-8 w-8 text-gray-400" aria-hidden="true" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Who's watching</h1>
              <p className="mt-1 text-gray-600">
                Profiles let more than one person share this account without mixing up
                what each of them is partway through.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <p className="mb-6 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          These are labels on your own account - nobody is invited and no second login is
          created. Each one remembers its own place in every title, so if someone else
          stops halfway through a film it will not appear in your Continue watching.
        </p>

        <p className="mb-6 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          A limit controls what that profile can <strong>watch</strong>. It can only make a
          profile stricter than the account already is, so it cannot be used to unlock
          anything. Comments and live chat are still governed by the account's own age,
          not by the profile.
        </p>

      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="new-profile">
          Add a profile
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="new-profile"
            type="text"
            value={newName}
            maxLength={40}
            placeholder="e.g. Kids, or Mum"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-orange-500"
          />
          <button
            type="button"
            onClick={() => void add()}
            disabled={adding || !newName.trim()}
            className="inline-flex items-center justify-center rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Any name you like - it is only shown to you. Two profiles on this account cannot
          share a name, but other accounts are free to use the same one.
        </p>
      </div>

      {loadError && (
        <div data-testid="profiles-load-error" className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p>{loadError}</p>
          <button type="button" onClick={() => void load()} className="mt-2 font-medium underline hover:no-underline">
            Try again
          </button>
        </div>
      )}

      {loading && !loadError && <p className="text-sm text-gray-500">Loading…</p>}

      {!loading && !loadError && (
        <ul className="space-y-2">
          {profiles.map((profile) => {
            const isActive = activeId ? profile.id === activeId : profile.isDefault;
            return (
              <li
                key={profile.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm"
              >
                {editingId === profile.id ? (
                  <>
                    <input
                      type="text"
                      value={editingName}
                      maxLength={40}
                      autoFocus
                      aria-label={`New name for ${profile.name}`}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveName(profile);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                    />
                    <button type="button" aria-label="Save name" onClick={() => void saveName(profile)} className="p-1 text-green-600 hover:text-green-700">
                      <Check className="h-4 w-4" />
                    </button>
                    <button type="button" aria-label="Cancel rename" onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate text-sm font-medium text-gray-900">
                      {profile.name}
                      {profile.isDefault && (
                        <span className="ml-2 text-xs font-normal text-gray-500">main</span>
                      )}
                    </span>

                    {isActive ? (
                      <span className="text-xs font-medium text-orange-600">Watching as this</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => watchAs(profile)}
                        className="text-xs text-gray-600 underline hover:text-orange-600"
                      >
                        Watch as this
                      </button>
                    )}

                    <label className="sr-only" htmlFor={`maturity-${profile.id}`}>
                      What {profile.name} can watch
                    </label>
                    <select
                      id={`maturity-${profile.id}`}
                      value={profile.maxRating ?? ''}
                      disabled={busyId === profile.id}
                      onChange={(e) => void changeMaturity(profile, e.target.value as MaturityRating | '')}
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-transparent focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      <option value="">No limit</option>
                      {MATURITY_RATINGS.map((rating) => (
                        <option key={rating} value={rating}>{rating} and under</option>
                      ))}
                    </select>

                    <button
                      type="button"
                      aria-label={`Rename ${profile.name}`}
                      disabled={busyId === profile.id}
                      onClick={() => { setEditingId(profile.id); setEditingName(profile.name); }}
                      className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${profile.name}`}
                      disabled={busyId === profile.id}
                      onClick={() => setConfirmRemove(profile)}
                      className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        isOpen={confirmRemove !== null}
        title={`Remove ${confirmRemove?.name ?? ''}?`}
        message={
          'This also discards wherever they had got to in everything they were part-way '
          + 'through. It cannot be undone.'
        }
        confirmText="Remove"
        danger
        loading={busyId !== null && busyId === confirmRemove?.id}
        onConfirm={() => { if (confirmRemove) void remove(confirmRemove); }}
        onClose={() => setConfirmRemove(null)}
      />
      </div>
    </div>
    </MainLayout>
  );
};

export default ProfilesPage;
